/**
 * WEATHER DECK — face visuals shared by the weather card overlay, the weather panel, the
 * legend, the results and the rulebook: one colour per face (lighter = mild,
 * darker = severe), a line-art icon per face, a small face chip and the
 * legend of the six faces with their card counts ("Light Rain · 3 of 20").
 */

export const FACE_VISUALS = {
  clear_skies: { bg: "#F3E3A6", fg: "#7A5C1A" },
  light_rain: { bg: "#8DB6C3", fg: "#1E3A44" },
  heavy_rain: { bg: "#31697E", fg: "#F2F7F8" },
  storm_surge: { bg: "#1F3D4C", fg: "#F2F7F8" },
  heat_wave: { bg: "#F2B26B", fg: "#5A2E0E" },
  heat_storm: { bg: "#B4521F", fg: "#FFF4E8" },
};
// Old saves / logs.
FACE_VISUALS.sunny_day = FACE_VISUALS.clear_skies;
FACE_VISUALS.extreme_heat_wave = FACE_VISUALS.heat_storm;

export const FACE_NAMES = {
  clear_skies: "Clear Skies",
  light_rain: "Light Rain",
  heavy_rain: "Heavy Rain",
  heat_wave: "Heat Wave",
  heat_storm: "Heat Storm",
  storm_surge: "Storm Surge",
};

export const visualOf = (key) => FACE_VISUALS[key] || FACE_VISUALS.light_rain;

const CLOUD = "M28 48 a12 12 0 0 1 4 -23 a16 16 0 0 1 30 -4 a12 12 0 0 1 12 27 z";

// Line-art icon per face (viewBox 0 0 100 100, stroke = currentColor).
export function WeatherIcon({ face, size = 96 }) {
  const common = { fill: "none", stroke: "currentColor", strokeWidth: 3.5, strokeLinecap: "round", strokeLinejoin: "round" };
  let body;
  switch (face) {
    case "clear_skies":
    case "sunny_day":
      body = (
        <g {...common}>
          <circle cx="50" cy="50" r="15" />
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i / 8) * Math.PI * 2;
            return <path key={i} d={`M${50 + 23 * Math.cos(a)} ${50 + 23 * Math.sin(a)} L${50 + 33 * Math.cos(a)} ${50 + 33 * Math.sin(a)}`} />;
          })}
        </g>
      );
      break;
    case "light_rain":
      body = (
        <g {...common}>
          <path d={CLOUD} />
          <path d="M36 60 l-3 9 M52 60 l-3 9 M68 60 l-3 9" />
        </g>
      );
      break;
    case "heavy_rain":
      body = (
        <g {...common}>
          <path d={CLOUD} />
          <path d="M30 58 l-5 18 M42 58 l-5 18 M54 58 l-5 18 M66 58 l-5 18 M78 58 l-5 18" />
        </g>
      );
      break;
    case "heat_wave":
      body = (
        <g {...common}>
          <path d="M44 22 a6 6 0 0 1 12 0 v32 a12 12 0 1 1 -12 0 z" />
          <path d="M50 40 v22" />
          <path d="M66 34 q5 -5 10 0 t10 0 M66 48 q5 -5 10 0 t10 0" />
        </g>
      );
      break;
    case "heat_storm":
    case "extreme_heat_wave":
      body = (
        <g {...common}>
          <circle cx="50" cy="34" r="13" />
          {Array.from({ length: 6 }).map((_, i) => {
            const a = Math.PI + (i / 5) * Math.PI;
            return <path key={i} d={`M${50 + 19 * Math.cos(a)} ${34 + 19 * Math.sin(a)} L${50 + 27 * Math.cos(a)} ${34 + 27 * Math.sin(a)}`} />;
          })}
          <path d="M18 62 q8 -8 16 0 t16 0 t16 0 t16 0 M18 76 q8 -8 16 0 t16 0 t16 0 t16 0" />
        </g>
      );
      break;
    default:
      body = (
        <g {...common}>
          <path d="M18 60 q8 -8 16 0 t16 0 t16 0 t16 0 M18 74 q8 -8 16 0 t16 0 t16 0 t16 0" />
          <path d="M54 16 l-10 22 h14 l-10 20" />
        </g>
      );
  }
  return (
    <svg viewBox="0 0 100 100" width={size} height={size} aria-hidden="true">
      {body}
    </svg>
  );
}

// A face as a small tinted square with its icon (size = side in px).
export function FaceChip({ faceKey, size = 28, radius = 7, reveal = false, testid }) {
  const v = visualOf(faceKey);
  return (
    <span
      data-testid={testid}
      data-face={faceKey}
      className={`shrink-0 inline-flex items-center justify-center ${reveal ? "weather-die-reveal" : ""}`}
      style={{ width: size, height: size, borderRadius: radius, background: v.bg, color: v.fg }}
    >
      <WeatherIcon face={faceKey} size={Math.round(size * 0.72)} />
    </span>
  );
}

// The six faces with their card counts; lit = the drawn / last face (others fade when one is lit).
// Props: faces (the six with their starting `count`), lit (face key), removed (the public
// `weather_removed` log: what left the deck at each degree). The count shown per face is the live
// one = starting count - everything removed so far (deck + discard + the card on the table; the
// deck order stays hidden), so it needs no hidden information.
export function WeatherLegend({ faces: base, lit = null, removed = [] }) {
  if (!base?.length) return null;
  const gone = {};
  (removed || []).forEach((r) => Object.entries(r.removed || {}).forEach(([k, n]) => { gone[k] = (gone[k] || 0) + n; }));
  const faces = base.map((f) => ({ ...f, count: Math.max(0, (f.count ?? 0) - (gone[f.key] || 0)) }));
  const sum = faces.reduce((n, f) => n + f.count, 0);
  return (
    <div className="grid gap-y-0.5 rounded-[8px] border border-[color:var(--line)] bg-[color:var(--card)] p-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))", columnGap: 8 }} data-testid="deck-legend">
      {faces.map((f) => {
        const on = lit === f.key;
        const pct = sum ? Math.round(((f.count ?? 0) / sum) * 100) : 0;
        return (
          <div
            key={f.key}
            data-testid={`deck-legend-${f.key}`}
            data-lit={on ? "true" : "false"}
            className="flex items-center min-w-0 rounded-[5px]"
            style={{ gap: 5, padding: "1px 2px", outline: on ? "2px solid var(--ink)" : undefined, outlineOffset: 0, opacity: lit && !on ? 0.55 : 1 }}
            title={`${f.name}: ${f.count ?? 0} of ${sum} cards (${pct}%) — ${f.description}`}
          >
            <FaceChip faceKey={f.key} size={16} radius={4} />
            <span className="min-w-0 truncate" style={{ fontSize: 11, lineHeight: "15px", color: "var(--ink)" }}>
              <span style={{ fontWeight: 600 }}>{f.name}</span>
              <span className="tabular-nums" style={{ color: "var(--ink-3)" }}> · {f.count ?? 0}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
